/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable unicorn/consistent-function-scoping */
import { DestroyRef, inject, Injectable } from '@angular/core';
import { selectCurrentUserProfile } from '@libs/store';
import { ConfirmDialogComponent, ConfirmDialogResult, Person } from '@libs/ui-lib';
import {
  DataState,
  DISABLE_GLOBAL_ERROR_TOASTS,
  EMAIL_REGEX_PATTERN,
  FfdComponentStore,
  Loadable,
  multiDownload,
  phoneNumberValidator,
  routes,
  toE164PhoneNumber,
} from '@libs/utils';
import { Store } from '@ngrx/store';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Contract,
  ContractsDataService,
  ContractsExternalDataService,
  ContractStatus,
  DownloadSignedPdf200Response,
  InstallOptionEnum,
  ItProviderInfo,
  PmsPreference,
  SendContractForSignatory,
  SiteInstallRequirements,
  ToolPreference,
  ToolsResponse,
  UpdateContract,
} from '@ffd/api-client';
import { tapResponse } from '@ngrx/operators';
import { iif, Observable, of, switchMap, retry } from 'rxjs';
import { HttpContext } from '@angular/common/http';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Dialog } from '@angular/cdk/dialog';
import { TagPusherService, TagEvent } from '@libs/shared-services';
import {
  AgreementConfirmationDialogComponent,
  AgreementDialogData,
} from '../agreement-confirmation-modal/agreement-confirmation-modal.component';
import { ItProviderDraft, toItProviderDetails } from '../it-provider-details/it-provider-details.mapper';
import { StepState } from './components/navigation-step/navigation-step.component';
import { getSiteInstallRequirementsFromNavigation } from './site-install-requirements-navigation-state';
import { CONTRACT_SIGN_TOKEN } from './site-setup-constants';
import { StepNumberEnum } from './step-number.enum';
export { StepNumberEnum } from './step-number.enum';

// Pre-fill the country code so users only type digits, matching the FFD Notes registration form.
export const DEFAULT_SIGNER_PHONE_VALUE = '+1 ';

export interface SiteSetupVM {
  currentStepFromContract: number;
  currentStepFromUser: number;
  stepOrder: string[][];
  currentUser: Person | null;
  contract?: Loadable<Contract | null>;
  isFormSubmitted: boolean;
  signBlob: Blob | null;
  form: SiteSetupForm;
  tokenError: boolean;
  tools?: Loadable<ToolsResponse | null>;
  siteInstallRequirements?: Loadable<SiteInstallRequirements | null>;
  isUnsupportedPms: boolean;
  itProviderDraft: ItProviderDraft;
}

interface SiteSetupState {
  currentStepFromContract: number;
  currentStepFromUser: number;
  contract?: Loadable<Contract | null>;
  token: string;
  isFormSubmitted: boolean;
  signBlob: Blob | null;
  tokenError: boolean;
  tools?: Loadable<ToolsResponse | null>;
  siteInstallRequirements?: Loadable<SiteInstallRequirements | null>;
  itProviderDraft: ItProviderDraft;
}

export type SiteSetupForm = FormGroup<{
  practice: FormGroup<{
    legalName: FormControl;
    country: FormControl;
    address: FormControl;
    city: FormControl;
    state: FormControl;
    zip: FormControl;
  }>;
  signer: FormGroup<{
    signatoryFirstName: FormControl;
    signatoryLastName: FormControl;
    signatoryEmail: FormControl;
    signatoryMobilePhone: FormControl;
  }>;
  confirmAuthorized: FormControl;
}>;

const initialState: SiteSetupState = {
  currentStepFromContract: 1,
  currentStepFromUser: 1,
  token: '',
  isFormSubmitted: false,
  signBlob: null,
  tokenError: false,
  itProviderDraft: {},
};

export const stepOrder = [
  [routes.compatibility],
  // [routes.overview], // Fast pass 2 skip this step
  // [routes.softwareLicense, routes.softwareLicenseVideo], // Fast pass 2 skip this step
  [routes.softwareLicense, routes.softwareLicenseAgreement],
  [routes.waitlistBlocker],
  // [routes.serverInstallation, routes.serverInstallationVideo], // Fast pass 2 skip this step
  [routes.serverInstallation, routes.checklist],
  [routes.serverInstallation, routes.download],
  [routes.practiceManager],
  // Appended for StepNumberEnum.ItProviderDetails (7), InstallOptions (8) and ItProviderContacted (9).
  [routes.itProviderDetails],
  [routes.installOptions],
  [routes.itProviderContacted],
];

// The backend accepts every PMS, so the supported ones are decided here. Anything else is held at the installation-pending step.
// A missing selectedPms is not blocked, so a practice that never reached the PMS question is not locked out.
const SUPPORTED_PMS = new Set<PmsPreference>([PmsPreference.DENTRIX, PmsPreference.OPEN_DENTAL, PmsPreference.EAGLESOFT]);

// Reads the PMS the user declared during the compatibility step — inside /setup there is no connected site yet,
// so SiteSettings.pmsType (and therefore selectSiteSettingsPmsType) is always null here.
export const isUnsupportedPms = (siteInstallRequirements?: SiteInstallRequirements | null): boolean => {
  const selectedPms = siteInstallRequirements?.selectedPms;

  return !!selectedPms && !SUPPORTED_PMS.has(selectedPms);
};

const stepOrderMap = new Map<StepNumberEnum, number>([
  [StepNumberEnum.Compatibility, 1],
  // [StepNumberEnum.Overview, 2], // Fast pass 2 skip this step
  // [StepNumberEnum.SoftwareLicenseVideo, 3], // Fast pass 2 skip this step
  [StepNumberEnum.SoftwareLicenseAgreement, 2],
  [StepNumberEnum.InstallationPending, 3],
  // [StepNumberEnum.ServerInstallationVideo, 5], // Fast pass 2 skip this step
  [StepNumberEnum.ServerInstallationChecklist, 4],
  [StepNumberEnum.ServerInstallationDownload, 5],
  [StepNumberEnum.PracticeManager, 6],
  // Display positions are approximate for now (progress-bar ordering is a styling-pass concern).
  [StepNumberEnum.ItProviderDetails, 7],
  [StepNumberEnum.InstallOptions, 8],
  [StepNumberEnum.ItProviderContacted, 9],
]);

@Injectable()
export class SiteSetupComponentService extends FfdComponentStore<SiteSetupState, SiteSetupVM> {
  public store = inject(Store);
  public activatedRoute = inject(ActivatedRoute);
  public router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(Dialog);
  private readonly tagPusherService = inject(TagPusherService);

  public contractsDataService = inject(ContractsDataService);
  public contractsExternalDataService = inject(ContractsExternalDataService);

  private readonly profile = this.store.selectSignal(selectCurrentUserProfile);
  public contractSignTokenSignal = this.selectSignal((state) => state.token);
  public currentStepFromContract = this.selectSignal((state) => state.currentStepFromContract);
  public currentStepFromContract$ = this.select((state) => state.currentStepFromContract);
  public queryParams = toSignal(this.activatedRoute.queryParams);
  // Read while the navigation that creates the setup flow is still running; it is gone by the time init() runs.
  private readonly siteInstallRequirementsFromNavigation = getSiteInstallRequirementsFromNavigation(this.router.currentNavigation());

  public isExternal = false;
  public licenseAgreementForm = new FormGroup({
    practice: new FormGroup({
      legalName: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      country: new FormControl('USA', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      address: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      city: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      state: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      zip: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    }),
    signer: new FormGroup({
      signatoryFirstName: new FormControl(this.profile()?.firstName ?? '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      signatoryLastName: new FormControl(this.profile()?.lastName ?? '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      signatoryEmail: new FormControl('', {
        validators: [Validators.required, Validators.pattern(EMAIL_REGEX_PATTERN)],
      }),
      signatoryMobilePhone: new FormControl(DEFAULT_SIGNER_PHONE_VALUE, {
        nonNullable: true,
        validators: [Validators.required, phoneNumberValidator()],
      }),
    }),
    confirmAuthorized: new FormControl(false, {
      validators: [Validators.requiredTrue],
    }),
  });

  public getLicenseAgreementForm(): SiteSetupForm {
    return this.licenseAgreementForm;
  }

  public setShowSignerEmail(withEmail: boolean): void {
    if (withEmail) {
      this.licenseAgreementForm.controls.signer.controls.signatoryEmail.setValidators([
        Validators.required,
        Validators.pattern(EMAIL_REGEX_PATTERN),
      ]);
    } else {
      this.licenseAgreementForm.controls.signer.controls.signatoryEmail.clearValidators();
    }
    this.licenseAgreementForm.controls.signer.controls.signatoryEmail.updateValueAndValidity();
  }

  public showAuthorizedCheckbox(withCheckbox: boolean): void {
    if (withCheckbox) {
      this.licenseAgreementForm.controls.confirmAuthorized.setValidators([Validators.requiredTrue]);
    } else {
      this.licenseAgreementForm.controls.confirmAuthorized.clearValidators();
    }
    this.licenseAgreementForm.updateValueAndValidity();
  }

  constructor() {
    super(initialState);
  }

  public override init(contractSignToken?: string): void {
    if (contractSignToken) {
      this.setToken(contractSignToken);
      this.isExternal = true;
    }
    this.getTools();
    if (this.siteInstallRequirementsFromNavigation) {
      this.onSiteInstallRequirementsLoaded(this.siteInstallRequirementsFromNavigation);
    } else {
      this.getToolPreference();
    }

    // Fast pass 2 pms redirectio
    const fromForm = this.queryParams()?.['fromForm'] ?? '';
    const queryParams = fromForm === 'true' ? { queryParams: { fromForm: fromForm } } : {};

    console.warn('init setup', fromForm);

    this.currentStepFromContract$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((step) => {
      if (!this.state().token) {
        //we only need to navigate if we are not in an external sign flow

        // If contract is already signed, use determineNextStep to account for unsupported PMS
        const contract = this.state().contract?.data;
        const siteInstallRequirements = this.state().siteInstallRequirements?.data;

        if (contract?.contractStatus === ContractStatus.SIGNED && siteInstallRequirements && step >= 2) {
          const nextStep = this.determineNextStep();
          this.setActiveStep(nextStep);
          this.router.navigate([routes.setup, ...stepOrder[nextStep - 1]], queryParams);
        } else {
          this.router.navigate([routes.setup, ...stepOrder[step - 1]], queryParams);
        }
      }
    });
  }

  protected override mapStateToViewModel(state: SiteSetupState): SiteSetupVM {
    const profile = this.profile();
    return {
      ...state,
      stepOrder,
      currentUser: profile,
      form: this.licenseAgreementForm,
      isUnsupportedPms: isUnsupportedPms(state.siteInstallRequirements?.data),
    };
  }

  public compatibilityScreenData = this.select((state) => ({
    contract: state.contract,
    selectSiteInstallRequirements: state.siteInstallRequirements,
  }));

  public setActiveStep(step: number): void {
    this.patchState({ currentStepFromUser: step });
  }

  public setItProviderDraft = this.updater((state, itProviderDraft: ItProviderDraft) => ({ ...state, itProviderDraft }));

  private readonly saveItProviderInfoEffect = this.effect<ItProviderInfo>(($) =>
    $.pipe(
      switchMap((body) =>
        this.contractsDataService.saveItProviderInfo(body).pipe(
          tapResponse(
            () => {
              // Nothing to update locally; step changes and navigation are handled by the callers.
            },
            (error) => console.error(error),
          ),
        ),
      ),
    ),
  );

  // IT-provider-details step, "I have an IT provider": keep the captured fields and go to install options.
  public continueToInstallOptions(draft: ItProviderDraft): void {
    this.setItProviderDraft(draft);
    this.setContractActiveStep({ step: StepNumberEnum.InstallOptions });
    this.setActiveStep(StepNumberEnum.InstallOptions);
    this.router.navigate([routes.setup, routes.installOptions]);
  }

  // Self-install path ("no IT provider" on step 1, or "install myself" on step 2): record the choice
  // (plus any captured IT fields) and continue into the existing server-installation checklist flow.
  public chooseSelfInstall(draft?: ItProviderDraft): void {
    this.saveItProviderInfoEffect({ installOptions: InstallOptionEnum.SELF_INSTALL, itProvider: toItProviderDetails(draft) });
    this.setContractActiveStep({ step: StepNumberEnum.ServerInstallationChecklist });
    this.setActiveStep(StepNumberEnum.ServerInstallationChecklist);
    this.router.navigate([routes.setup, routes.serverInstallation, routes.checklist]);
  }

  // Contact-my-IT-provider path: record the choice + IT fields, then advance to the terminal
  // "we'll contact your IT team" step. Persisting the step means a /setup reload resumes on that
  // read-only screen instead of re-offering the install options (which would re-submit).
  public chooseContactItProvider(draft: ItProviderDraft): void {
    this.saveItProviderInfoEffect({ installOptions: InstallOptionEnum.CONTACT_IT_PROVIDER, itProvider: toItProviderDetails(draft) });
    this.setContractActiveStep({ step: StepNumberEnum.ItProviderContacted });
    this.setActiveStep(StepNumberEnum.ItProviderContacted);
    this.router.navigate([routes.setup, routes.itProviderContacted]);
  }

  public signForm(signBlob: Blob | null): void {
    this.patchState({ signBlob });
  }

  public submitForm(isAuthorized: boolean): void {
    this.patchState({ isFormSubmitted: true });
    if (this.licenseAgreementForm.valid) {
      const contractFromForm = {
        address: this.licenseAgreementForm.controls.practice.controls.address.value,
        city: this.licenseAgreementForm.controls.practice.controls.city.value,
        country: this.licenseAgreementForm.controls.practice.controls.country.value,
        legalName: this.licenseAgreementForm.controls.practice.controls.legalName.value,
        zip: this.licenseAgreementForm.controls.practice.controls.zip.value,
        state: this.licenseAgreementForm.controls.practice.controls.state.value,
        signatoryFirstName: this.licenseAgreementForm.controls.signer.controls.signatoryFirstName.value,
        signatoryLastName: this.licenseAgreementForm.controls.signer.controls.signatoryLastName.value,
        signatoryMobilePhone: toE164PhoneNumber(this.licenseAgreementForm.controls.signer.controls.signatoryMobilePhone.value),
      };
      if (isAuthorized) {
        this.openConfirmationModal().subscribe((isConfirmed) => {
          if (!isConfirmed) return;
          const nextStep = this.determineNextStep();
          this.saveContract({ newContract: { ...contractFromForm, step: nextStep }, shouldSign: true });
        });
      } else {
        this.tagPusherService.pushTag({
          event: TagEvent.LicenseAgreementAgreementSend,
          contract_id: this.state().contract?.data?.id,
        });

        this.sendToSignatory({
          contractDetails: {
            ...contractFromForm,
            signatoryEmail: this.licenseAgreementForm.controls.signer.controls.signatoryEmail.value!,
          },
        });
      }
    }
  }

  public submitExternalForm(): void {
    this.patchState({ isFormSubmitted: true });
    if (this.licenseAgreementForm.valid) {
      const contractFromForm = {
        address: this.licenseAgreementForm.controls.practice.controls.address.value,
        city: this.licenseAgreementForm.controls.practice.controls.city.value,
        country: this.licenseAgreementForm.controls.practice.controls.country.value,
        legalName: this.licenseAgreementForm.controls.practice.controls.legalName.value,
        zip: this.licenseAgreementForm.controls.practice.controls.zip.value,
        state: this.licenseAgreementForm.controls.practice.controls.state.value,
        signatoryFirstName: this.licenseAgreementForm.controls.signer.controls.signatoryFirstName.value,
        signatoryLastName: this.licenseAgreementForm.controls.signer.controls.signatoryLastName.value,
        signatoryMobilePhone: toE164PhoneNumber(this.licenseAgreementForm.controls.signer.controls.signatoryMobilePhone.value),
      };
      this.openConfirmationModal().subscribe((isConfirmed) => {
        if (!isConfirmed) return;
        this.saveExternalContract({
          newContract: { ...contractFromForm, step: this.viewModel().currentStepFromContract },
          shouldSign: true,
        });
      });
    }
  }

  private openConfirmationModal(): Observable<boolean | undefined> {
    const address = this.licenseAgreementForm.value.practice?.address ?? '';
    const country = this.licenseAgreementForm.value.practice?.country ?? '';
    const city = this.licenseAgreementForm.value.practice?.city ?? '';
    const state = this.licenseAgreementForm.value.practice?.state ?? '';
    const zip = this.licenseAgreementForm.value.practice?.zip ?? '';
    const dialogData: AgreementDialogData = {
      practiceName: this.licenseAgreementForm.value.practice?.legalName ?? '',
      address: address ? `${address}, ${city}, ${state}, ${zip}, ${country}` : '',
      signerName: `${this.licenseAgreementForm.value.signer?.signatoryFirstName} ${this.licenseAgreementForm.value.signer?.signatoryLastName}`,
    };
    return this.dialog.open<boolean>(AgreementConfirmationDialogComponent, {
      data: dialogData,
      width: '500px',
      backdropClass: ['welcome-video-backdrop', 'backdrop-blur-overlay', 'bg-overlay-light'],
    }).closed;
  }

  private openComeBackLaterModal(): void {
    this.dialog
      .open<ConfirmDialogResult>(ConfirmDialogComponent, {
        backdropClass: ['bg-overlay-light'],
        panelClass: ['w-[390px]'],
        disableClose: true,
        data: {
          iconKey: 'px-24-message',
          title: 'site_setup_come_back_later_title',
          description: 'site_setup_come_back_later_description',
          confirmLabel: 'site_setup_come_back_later_confirm',
        },
      })
      .closed.subscribe(() => {
        this.router.navigate([routes.site, routes.showcaseSiteId]);
      });
  }

  public setToken = this.updater((state, token: string) => {
    return { ...state, token };
  });

  public getStepNumberToDisplay(step: number): number {
    return stepOrderMap.get(step) ?? 1;
  }

  public getStepState(step: number): StepState[] {
    const stepsWithoutContract = new Set([StepNumberEnum.Compatibility]);

    const stepNumber = stepOrderMap.get(step) ?? 1;
    const currentStepFromUser = stepOrderMap.get(this.viewModel().currentStepFromUser) ?? 1;
    let currentStepFromContract = stepOrderMap.get(this.viewModel().currentStepFromContract) ?? 1;
    if (this.state().contract?.data === null && stepsWithoutContract.has(stepNumber)) {
      currentStepFromContract = currentStepFromUser;
    }

    const stepState = [];
    if (stepNumber === currentStepFromUser) {
      stepState.push(StepState.Active);
    }

    if (stepNumber < currentStepFromContract) {
      stepState.push(StepState.Completed);
    } else if (stepNumber === currentStepFromContract) {
      stepState.push(StepState.CurrentInContract);
    } else {
      stepState.push(StepState.Disabled);
    }
    return stepState;
  }

  public getContract = this.effect(($) => {
    this.patchState({ contract: { data: null, state: DataState.Loading } });

    return $.pipe(
      switchMap(() => {
        return this.contractsDataService.getContracts().pipe(
          tapResponse(
            (response) => {
              // now we use the first value from the response
              const contract = response[0];

              this.patchState({
                contract: { data: contract ?? null, state: DataState.Fetched },
                currentStepFromContract: contract ? contract.step : 1,
                currentStepFromUser: contract ? contract.step : 1,
              });
              this.fillFormFromContract(contract);
            },
            (error) => {
              console.error(error);
              this.patchState({ contract: { data: null, state: DataState.Error } });
            },
          ),
        );
      }),
    );
  });

  public beginContract = this.effect(($) => {
    return $.pipe(
      switchMap(() => {
        return iif(() => !!this.state().contract?.data, of(this.state().contract?.data), this.contractsDataService.createContract({})).pipe(
          tapResponse(
            (contract) => {
              if (!this.state().contract?.data && contract) {
                this.tagPusherService.pushTag({
                  event: TagEvent.SiteInstallationBegin,
                  contract_id: contract.id,
                });
              }

              this.patchState({
                contract: { data: contract, state: DataState.Fetched },
              });
              this.setContractActiveStep({
                step: StepNumberEnum.SoftwareLicenseAgreement,
              });
              this.setActiveStep(StepNumberEnum.SoftwareLicenseAgreement);
              this.router.navigate([routes.setup, routes.softwareLicense, routes.softwareLicenseAgreement]);
            },
            (error) => {
              console.error(error);
            },
          ),
        );
      }),
    );
  });

  public saveContract = this.effect<{ newContract: UpdateContract; shouldSign?: boolean }>(($) => {
    return $.pipe(
      switchMap(({ newContract, shouldSign }) => {
        const contract = this.state().contract?.data;
        this.patchState({ contract: { data: contract, state: DataState.Loading } });
        return this.contractsDataService.updateContract(contract?.id ?? '', newContract).pipe(
          tapResponse(
            (responseContract) => {
              this.patchState({
                contract: { data: responseContract, state: DataState.Fetched },
                currentStepFromContract: responseContract.step,
              });
              if (shouldSign) {
                this.signContract({
                  file: this.state().signBlob ?? new Blob(),
                });
              }
            },
            (error) => {
              console.error(error);
            },
          ),
        );
      }),
    );
  });

  public saveExternalContract = this.effect<{ newContract: UpdateContract; shouldSign?: boolean }>(($) => {
    return $.pipe(
      switchMap(({ newContract, shouldSign }) => {
        const contract = this.state().contract?.data;
        this.patchState({ contract: { data: contract, state: DataState.Loading } });
        return this.contractsExternalDataService
          .updateContractExternal(newContract, undefined, undefined, {
            context: new HttpContext().set(CONTRACT_SIGN_TOKEN, this.contractSignTokenSignal()),
          })
          .pipe(
            tapResponse(
              (responseContract) => {
                this.patchState({
                  contract: { data: responseContract, state: DataState.Fetched },
                  currentStepFromContract: responseContract.step,
                });
                if (shouldSign) {
                  this.signExternalContract({
                    file: this.state().signBlob ?? new Blob(),
                  });
                }
              },
              (error) => {
                console.error(error);
              },
            ),
          );
      }),
    );
  });

  public setContractActiveStep = this.effect<{ step: StepNumberEnum }>(($) => {
    return $.pipe(
      switchMap(({ step }) => {
        const contract = this.state().contract?.data;
        return this.contractsDataService
          .updateContract(contract?.id ?? '', {
            step,
            address: contract?.address ?? '',
            city: contract?.city ?? '',
            country: contract?.country ?? '',
            legalName: contract?.legalName ?? '',
            signatoryFirstName: contract?.signatoryFirstName ?? '',
            signatoryLastName: contract?.signatoryLastName ?? '',
            signatoryMobilePhone: contract?.signatoryMobilePhone ?? '',
            state: contract?.state ?? '',
            zip: contract?.zip ?? '',
          })
          .pipe(
            tapResponse(
              (responseContract) => {
                this.patchState({
                  contract: { data: responseContract, state: DataState.Fetched },
                  currentStepFromContract: responseContract.step,
                });
              },
              (error) => {
                console.error(error);
              },
            ),
          );
      }),
    );
  });

  private readonly signContract = this.effect<{ file: Blob }>(($) => {
    return $.pipe(
      switchMap(({ file }) => {
        const contract = this.state().contract?.data;
        this.patchState({ contract: { data: contract, state: DataState.Loading } });
        return this.contractsDataService.signContract(contract?.id ?? '', file).pipe(
          tapResponse(
            () => {
              this.tagPusherService.pushTag({
                event: TagEvent.LicenseAgreementFinished,
                contract_id: contract?.id,
              });

              this.getContract();
            },
            (error) => {
              console.error(error);
            },
          ),
        );
      }),
    );
  });

  public sendToSignatory = this.effect<{ contractDetails: SendContractForSignatory }>(($) => {
    return $.pipe(
      switchMap(({ contractDetails }) => {
        const contract = this.state().contract?.data;
        this.patchState({ contract: { data: contract, state: DataState.Loading } });
        return this.contractsDataService.sendEmailForSignatory(contract?.id ?? '', contractDetails).pipe(
          tapResponse(
            () => {
              this.tagPusherService.pushTag({
                event: TagEvent.LicenseAgreementAgreementSuccess,
                contract_id: contract?.id,
              });

              this.getContract();
            },
            (error) => {
              console.error(error);
            },
          ),
        );
      }),
    );
  });

  public downloadSignedPdf = this.effect(($) => {
    return $.pipe(
      switchMap(() => {
        const contract = this.state().contract?.data;
        return this.contractsDataService.downloadSignedPdf(contract?.id ?? '').pipe(
          tapResponse(
            async (response: DownloadSignedPdf200Response) => {
              try {
                await multiDownload([response.url]);
              } catch (error) {
                console.error('Error during download', error);
              }
            },
            (error) => {
              console.error('Error in downloadAttachment', error);
            },
          ),
        );
      }),
    );
  });

  public downloadSignedPdfExternal = this.effect(($) => {
    return $.pipe(
      switchMap(() => {
        return this.contractsExternalDataService
          .downloadSignedPdfExternal(undefined, undefined, {
            context: new HttpContext().set(CONTRACT_SIGN_TOKEN, this.contractSignTokenSignal()),
          })
          .pipe(
            tapResponse(
              async (response: DownloadSignedPdf200Response) => {
                try {
                  await multiDownload([response.url]);
                } catch (error) {
                  console.error('Error during download', error);
                }
              },
              (error) => {
                console.error('Error in downloadAttachment', error);
              },
            ),
          );
      }),
    );
  });

  public readonly getTools = this.effect(($) => {
    this.patchState({ tools: { data: null, state: DataState.Loading } });
    return $.pipe(
      switchMap(() => {
        return this.contractsDataService.getTools().pipe(
          tapResponse(
            (response) => {
              this.patchState({ tools: { data: response, state: DataState.Fetched } });
            },
            (error) => {
              console.error(error);
            },
          ),
        );
      }),
    );
  });

  public readonly getToolPreference = this.effect(($: Observable<boolean | void>) => {
    this.patchState({ siteInstallRequirements: { data: null, state: DataState.Loading } });
    return $.pipe(
      switchMap((afterSettingCompatibility) => {
        return this.contractsDataService
          .getSiteInstallRequirements(undefined, undefined, {
            context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
          })
          .pipe(
            retry({
              delay: 1000,
            }),
            tapResponse(
              (response) => {
                this.onSiteInstallRequirementsLoaded(response, afterSettingCompatibility === true);
              },
              (error) => {
                console.error(error);
              },
            ),
          );
      }),
    );
  });

  private onSiteInstallRequirementsLoaded(response: SiteInstallRequirements, afterSettingCompatibility = false): void {
    this.patchState({ siteInstallRequirements: { data: response, state: DataState.Fetched } });
    if (afterSettingCompatibility) {
      this.pushCompatibilityTag(response);
    }
    if (this.isExternal) {
      this.getExternalContract();
    } else {
      this.getContract();
    }
  }

  public readonly setToolPreference = this.effect<{ tools: ToolPreference }>(($) => {
    return $.pipe(
      switchMap(({ tools }) => {
        this.patchState({ siteInstallRequirements: { data: null, state: DataState.Loading } });
        return this.contractsDataService.setToolPreference(tools).pipe(
          tapResponse(
            () => {
              this.getToolPreference(true);
            },
            (error) => {
              console.error(error);
            },
          ),
        );
      }),
    );
  });

  public abandonContract = this.effect(($) => {
    return $.pipe(
      switchMap(() => {
        return this.contractsDataService.continueContractLater().pipe(
          tapResponse(
            () => {
              this.openComeBackLaterModal();
            },
            (error) => {
              console.error(error);
            },
          ),
        );
      }),
    );
  });

  public getExternalContract = this.effect(($) => {
    return $.pipe(
      switchMap(() => {
        return this.contractsExternalDataService
          .getContractExternal(undefined, undefined, {
            context: new HttpContext().set(CONTRACT_SIGN_TOKEN, this.contractSignTokenSignal()),
          })
          .pipe(
            tapResponse(
              (response) => {
                // now we use the first value from the response
                this.patchState({
                  contract: { data: response ?? null, state: DataState.Fetched },
                  currentStepFromContract: response ? response.step : 1,
                  currentStepFromUser: response ? response.step : 1,
                  tokenError: false,
                });
                this.fillFormFromContract(response);
              },
              (error) => {
                console.error(error);
                this.patchState({ tokenError: true });
              },
            ),
          );
      }),
    );
  });

  private readonly signExternalContract = this.effect<{ file: Blob }>(($) => {
    return $.pipe(
      switchMap(({ file }) => {
        const contract = this.state().contract?.data;
        this.patchState({ contract: { data: contract, state: DataState.Loading } });

        return this.contractsExternalDataService
          .signContractExternal(file, undefined, undefined, {
            context: new HttpContext().set(CONTRACT_SIGN_TOKEN, this.contractSignTokenSignal()),
          })
          .pipe(
            tapResponse(
              () => {
                this.getExternalContract();
              },
              (error) => {
                console.error(error);
              },
            ),
          );
      }),
    );
  });

  public readonly rejectExternalContract = this.effect(($) => {
    return $.pipe(
      switchMap(() => {
        return this.contractsExternalDataService.rejectSigningContractExternal(undefined, undefined, {
          context: new HttpContext().set(CONTRACT_SIGN_TOKEN, this.contractSignTokenSignal()),
        });
      }),
    );
  });

  private fillFormFromContract(contract?: Contract): void {
    const practice = this.licenseAgreementForm.controls.practice.controls;
    const signer = this.licenseAgreementForm.controls.signer.controls;
    if (contract?.legalName) practice.legalName.setValue(contract.legalName);
    if (contract?.country) practice.country.setValue(contract.country);
    if (contract?.address) practice.address.setValue(contract.address);
    if (contract?.city) practice.city.setValue(contract.city);
    if (contract?.state) practice.state.setValue(contract.state);
    if (contract?.zip) practice.zip.setValue(contract.zip);
    if (contract?.signatoryFirstName) signer.signatoryFirstName.setValue(contract.signatoryFirstName);
    if (contract?.signatoryLastName) signer.signatoryLastName.setValue(contract.signatoryLastName);
    if (contract?.signatoryMobilePhone) signer.signatoryMobilePhone.setValue(contract.signatoryMobilePhone);
  }

  private pushCompatibilityTag(response: SiteInstallRequirements): void {
    const reasons = [];
    if (!response.pmsAccepted) {
      reasons.push('PMS');
      if (!response.imagingAccepted) {
        reasons.push('Imaging tool');
      }
    }
    if (reasons.length > 0) {
      this.tagPusherService.pushTag({
        event: TagEvent.SiteInstallationNotCompatible,
        reason: `Not compatible: ${reasons.join(', ')}`,
      });
    }
  }

  public determineNextStep(): StepNumberEnum {
    const contract = this.state().contract?.data;

    const siteInstallRequirements = this.state().siteInstallRequirements?.data;
    const isPmsAccepted = siteInstallRequirements?.pmsAccepted === true;
    const isContractSigned = contract?.contractStatus === ContractStatus.SIGNED;

    // If PMS is not accepted, stay at compatibility check
    if (!isPmsAccepted) {
      return StepNumberEnum.Compatibility;
    }

    // If PMS is accepted but contract is not signed yet, start contract flow
    if (!isContractSigned) {
      return StepNumberEnum.SoftwareLicenseAgreement;
    }

    // Contract signed: practices on a PMS that is not supported yet see the blocker. Checked ahead of the contract.step
    // passthrough so practices that signed before this branch existed (and therefore carry a deeper contract.step) are
    // still re-routed here.
    if (this.isUnsupportedPms()) {
      return StepNumberEnum.InstallationPending;
    }

    const step = (contract?.step ?? 0) as StepNumberEnum;

    // Resume within the IT-provider mini-flow. These steps are appended to the enum (7, 8, 9) but come
    // BEFORE the installation steps in the UX, so they are matched explicitly rather than by number.
    // ItProviderContacted (9) is the terminal "we'll contact your IT team" screen for that path.
    if (
      step === StepNumberEnum.ItProviderDetails ||
      step === StepNumberEnum.InstallOptions ||
      step === StepNumberEnum.ItProviderContacted
    ) {
      return step;
    }

    // Preserve real installation progress (checklist reached or deeper) — keeps in-flight users put.
    if (
      step === StepNumberEnum.ServerInstallationChecklist ||
      step === StepNumberEnum.ServerInstallationDownload ||
      step === StepNumberEnum.PracticeManager
    ) {
      return step;
    }

    // Freshly signed (step still at/under the agreement): enter the new IT-provider flow first.
    return StepNumberEnum.ItProviderDetails;
  }

  private isUnsupportedPms(): boolean {
    return isUnsupportedPms(this.state().siteInstallRequirements?.data);
  }
}
